import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import CustomerDetails from "../section/CustomerDetails";
import Prescription from "../section/Prescription";
import AddPrescription from "../section/AddPrescription";

import api from "../../api/axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EYE_TEST_FIELDS = [
  "chief_complaint", "history",
  "vision_aided_r", "vision_aided_l",
  "vision_unaided_r", "vision_unaided_l",
  "pg_r_sph", "pg_r_cyl", "pg_r_axis", "pg_r_add",
  "pg_l_sph", "pg_l_cyl", "pg_l_axis", "pg_l_add",
  "pg_remarks",
  "ar1_r_sph", "ar1_r_cyl", "ar1_r_axis",
  "ar1_l_sph", "ar1_l_cyl", "ar1_l_axis",
  "ar2_r_sph", "ar2_r_cyl", "ar2_r_axis",
  "ar2_l_sph", "ar2_l_cyl", "ar2_l_axis",
  "subj_r_sph", "subj_r_cyl", "subj_r_axis", "subj_r_vn", "subj_r_add",
  "subj_l_sph", "subj_l_cyl", "subj_l_axis", "subj_l_vn", "subj_l_add",
  "dry_r_sph", "dry_r_cyl", "dry_r_axis", "dry_r_vn", "dry_r_add",
  "dry_l_sph", "dry_l_cyl", "dry_l_axis", "dry_l_vn", "dry_l_add",
  "pmt_r_sph", "pmt_r_cyl", "pmt_r_axis", "pmt_r_vn", "pmt_r_add",
  "pmt_l_sph", "pmt_l_cyl", "pmt_l_axis", "pmt_l_vn", "pmt_l_add",
  "eye_test_remarks",
  // New Lenses
  "lens_type", "lens_material", "lens_coating", "lens_tint",
  "lens_brand", "lens_cost", "lens_frame_condition", "lens_delivery_date",
  "lens_notes",
  // New Spectacles
  "spec_frame_model", "spec_frame_brand", "spec_frame_color",
  "spec_frame_material", "spec_frame_shape", "spec_frame_size",
  "spec_lens_type", "spec_lens_material", "spec_lens_coating",
  "spec_lens_tint", "spec_lens_brand",
  "spec_frame_cost", "spec_lens_cost", "spec_total",
  "spec_advance", "spec_balance",
  "spec_delivery_date", "spec_notes",
  // Spectacles Repair
  "repair_type", "repair_problem", "repair_parts",
  "repair_cost", "repair_delivery_date", "repair_status", "repair_notes",
];

const RECORD_TITLE = {
  "Only Visit": "Visit",
  "Eye Test": "Eye Test",
  "Lens Only": "Lens Order",
  "Frame Only": "Frame Order",
  "Complete Specs": "Spectacles Order",
  "Specs Repair": "Repair Ticket",
};

const emptyEyeTest = () =>
  EYE_TEST_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {});

const createPrescription = () => ({
  id: Date.now() + Math.random(),

  prescription_date: "",

  right_sph: "",
  right_cyl: "",
  right_axis: "",
  right_vn: "",

  left_sph: "",
  left_cyl: "",
  left_axis: "",
  left_vn: "",

  add_power: "",
  remarks: "",

  ...emptyEyeTest(),

  images: [],
});

const AddCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);

  // Customer Details
  const [customer, setCustomer] = useState({
    customer_name: "",
    phone: "",
    email: "",
    date_of_birth: "",
    age: "",
    favourite: false,
    branch: "",
    visit_for: "",
    address: "",
  });

  // Prescription List
  const [prescriptions, setPrescriptions] = useState([createPrescription()]);

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  // Fetch Customer (Edit Mode)
  const fetchCustomer = async () => {
    try {
      const { data } = await api.get(`/api/customers/${id}`);

      const customerData = data.customer;

      setCustomer({
        customer_name: customerData.customer_name || "",
        phone: customerData.phone || "",
        email: customerData.email || "",
        date_of_birth: customerData.date_of_birth || "",
        age: customerData.age || "",
        favourite: !!customerData.favourite,
        branch: customerData.branch || "",
        visit_for: customerData.visit_for || "",
        address: customerData.address || "",
      });

      const prescriptionData = data.prescriptions.map((item) => ({
        id: item.id,

        prescription_date: item.prescription_date || "",

        right_sph: item.right_sph || "",
        right_cyl: item.right_cyl || "",
        right_axis: item.right_axis || "",
        right_vn: item.right_vn || "",

        left_sph: item.left_sph || "",
        left_cyl: item.left_cyl || "",
        left_axis: item.left_axis || "",
        left_vn: item.left_vn || "",

        add_power: item.add_power || "",
        remarks: item.remarks || "",

        ...EYE_TEST_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: item[f] || "" }),
          {}
        ),

        // Wrap existing images with the shape the UI expects (preview, id, isExisting).
        // Without this, <img src={image.preview}/> would render broken and the save
        // loop would send `img.file = undefined`, corrupting the FormData.
        images: (item.images || []).map((img) => ({
          id: img.id,
          preview: `${API_URL}/api/uploads/customers/${img.image}`,
          original_name: img.original_name,
          isExisting: true,
        })),
      }));

      setPrescriptions(prescriptionData);
    } catch (error) {
      console.error("Fetch Customer Error:", error);
      alert(
        error.response?.data?.message ||
          "Unable to load customer details. Please try again."
      );
    }
  };

  // Add Prescription
  const handleAddPrescription = () => {
    setPrescriptions((prev) => [...prev, createPrescription()]);
  };

  // Remove Prescription
  const handleRemovePrescription = (id) => {
    setPrescriptions((prev) => prev.filter((item) => item.id !== id));
  };

  // Save Customer
  const handleSaveCustomer = async () => {
    if (loading) return;

    try {
      // Customer Validation
      if (!customer.customer_name.trim()) {
        return alert("Customer Name is required.");
      }

      if (!/^\d{10}$/.test(customer.phone.trim())) {
        return alert("Phone Number must be exactly 10 digits.");
      }

      if (
        customer.email.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())
      ) {
        return alert("Please enter a valid Email Address.");
      }

      if (!customer.branch) {
        return alert("Please select Branch.");
      }

      if (!customer.visit_for) {
        return alert("Please select Visit Purpose.");
      }

      if (prescriptions.length === 0) {
        return alert("Please add at least one prescription.");
      }

      // Prescription Validation
      for (let i = 0; i < prescriptions.length; i++) {
        const prescription = prescriptions[i];

        if (!prescription.prescription_date) {
          return alert(
            `Prescription ${i + 1}: Please select prescription date.`
          );
        }
      }

      setLoading(true);

      const formData = new FormData();

      // Customer
      formData.append(
        "customer",
        JSON.stringify({
          customer_name: customer.customer_name.trim(),
          phone: customer.phone.trim(),
          email: customer.email.trim(),
          date_of_birth: customer.date_of_birth,
          age: customer.age,
          favourite: customer.favourite,
          branch: customer.branch,
          visit_for: customer.visit_for,
          address: customer.address.trim(),
        })
      );

      // Prescription Data (Without Images)
      const prescriptionData = prescriptions.map((p) => ({
        prescription_date: p.prescription_date,

        right_sph: p.right_sph,
        right_cyl: p.right_cyl,
        right_axis: p.right_axis,
        right_vn: p.right_vn,

        left_sph: p.left_sph,
        left_cyl: p.left_cyl,
        left_axis: p.left_axis,
        left_vn: p.left_vn,

        add_power: p.add_power,
        remarks: p.remarks,

        ...EYE_TEST_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: p[f] || "" }),
          {}
        ),
      }));

      formData.append("prescriptions", JSON.stringify(prescriptionData));

      // Split images into NEW uploads and EXISTING (kept) images
      const imageMappings = [];
      const existingImages = [];
      let newFileIndex = 0;

      prescriptions.forEach((prescription, prescriptionIndex) => {
        (prescription.images || []).forEach((img) => {
          if (img.isExisting && img.id) {
            // Existing image the user chose to keep
            existingImages.push({
              prescriptionIndex,
              imageId: img.id,
            });
          } else if (img.file) {
            // Newly uploaded file
            formData.append("images", img.file);
            imageMappings.push({
              prescriptionIndex,
              imageIndex: newFileIndex,
            });
            newFileIndex += 1;
          }
          // any other case (no file, no id) is skipped safely
        });
      });

      formData.append("imageMappings", JSON.stringify(imageMappings));
      formData.append("existingImages", JSON.stringify(existingImages));

      let response;

      if (isEdit) {
        response = await api.put(`/api/customers/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await api.post("/api/customers", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      const { data } = response;

      alert(
        data.message ||
          (isEdit
            ? "Customer Updated Successfully."
            : "Customer Saved Successfully.")
      );

      // Cleanup image preview URLs (only object URLs of new uploads)
      prescriptions.forEach((prescription) => {
        (prescription.images || []).forEach((img) => {
          if (img.preview && !img.isExisting) {
            URL.revokeObjectURL(img.preview);
          }
        });
      });

      // Reset Form
      if (isEdit) {
        navigate("/customer");
      } else {
        setCustomer({
          customer_name: "",
          phone: "",
          email: "",
          date_of_birth: "",
          branch: "",
          visit_for: "",
          address: "",
        });

        setPrescriptions([createPrescription()]);
      }
    } catch (error) {
      console.error("Save Customer Error:", error);

      const message =
        error.response?.data?.message ||
        error.message ||
        "Unable to save customer. Please try again.";

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <CustomerDetails customer={customer} setCustomer={setCustomer} />

      <div className="mt-6 space-y-6">
        {prescriptions.map((item, index) => (
          <Prescription
            key={item.id}
            title={`${RECORD_TITLE[customer.visit_for] || "Prescription"} ${index + 1}`}
            index={index}
            data={item}
            setPrescriptions={setPrescriptions}
            canRemove={prescriptions.length > 1}
            onRemove={() => handleRemovePrescription(item.id)}
            visitFor={customer.visit_for}
          />
        ))}
      </div>

      <div className="mt-6">
        <AddPrescription
          onAdd={handleAddPrescription}
          onSave={handleSaveCustomer}
          loading={loading}
          visitFor={customer.visit_for}
        />
      </div>
    </div>
  );
};

export default AddCustomer;
